import { ChakraV3, Empty, Pagination, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { showModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useCreateParticipantNoteMutation } from '../../graphql/hooks/useCreateParticipantNoteMutation';
import { useParticipantNotesFilterQuery } from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { useParticipantNotesFilter } from '../../hooks/useParticipantNotesFilter';
import { ParticipantNoteCard } from './ParticipantNoteCard';
import { ParticipantNoteCreateModal } from './ParticipantNoteCreateModal';
import { ParticipantNoteEditModal } from './ParticipantNoteEditModal';
import { ParticipantNotesFilterControls } from './ParticipantNotesFilterControls';

interface Props {
  participantId: string;
  isDisabled?: boolean;
}

export const ParticipantNotes = (props: Props) => {
  const { participantId, isDisabled } = props;

  const { user, organization, successToast, errorToast } = useAppContext();
  const filter = useParticipantNotesFilter();
  const { participantNotes, pagination, isLoading, error } = useParticipantNotesFilterQuery(
    participantId,
    filter,
  );

  const mutation = useCreateParticipantNoteMutation();

  const onCreateNewBlankNote = React.useCallback(async () => {
    await mutation
      .mutateAsync({
        organizationId: organization.id,
        request: {
          id: undefined,
          participantId: participantId,
          userId: user.id,
          labelIds: [],
        },
      })
      .then((res) => {
        successToast('Note created successfully');
        showModal(ParticipantNoteEditModal, {
          participantId,
          participantNoteId: res.createParticipantNote.id,
          isDisabled,
        });
      })
      .catch((err) => {
        errorToast(`Error creating note: ${err.message}`);
      });
  }, [errorToast, isDisabled, mutation, organization.id, participantId, successToast, user.id]);

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError error={error} />;
    }

    if (P.isEmpty(participantNotes)) {
      return <Empty h='full' title='No notes found' />;
    }

    return (
      <ChakraV3.SimpleGrid minChildWidth='2xl' gap={8}>
        {participantNotes.map((participantNote) => (
          <ParticipantNoteCard
            key={participantNote.id}
            participantNote={participantNote}
            isDisabled={isDisabled}
          />
        ))}
      </ChakraV3.SimpleGrid>
    );
  });

  return (
    <ChakraV3.Stack gap={4} px={6} py={4}>
      <ChakraV3.HStack align='end'>
        <ParticipantNotesFilterControls filter={filter} />
        <ChakraV3.Spacer />
        <ChakraV3.HStack>
          <ChakraV3.Button
            onClick={() => showModal(ParticipantNoteCreateModal, { participantId })}
            colorPalette='brand'
            size='sm'
            disabled={isDisabled}
          >
            <FaPlus />
            Add note from template
          </ChakraV3.Button>
          <ChakraV3.Button
            onClick={() => onCreateNewBlankNote()}
            colorPalette='brand'
            size='sm'
            disabled={isDisabled}
          >
            <FaPlus />
            Add blank note
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </ChakraV3.HStack>
      {content}
      <ChakraV3.HStack>
        <ChakraV3.Spacer />
        {pagination && <Pagination {...pagination} />}
      </ChakraV3.HStack>
    </ChakraV3.Stack>
  );
};
